/**
 * When this operator last opened the Inbox, per browser.
 *
 * The badge counts escalations newer than this, so it behaves like a
 * notification while the `Needs a human` filter stays the full work queue. Per
 * browser is the right granularity for a badge and costs no schema; if it ever
 * has to agree across devices, only this file changes — the boundary already
 * travels to the server as a parameter.
 */

const VISIT_KEY = "versafy.inbox.lastVisitAt"

/**
 * Keyed per identity, not per browser: a shared workstation would otherwise hand
 * the next colleague a cleared badge, and a super user switching tenants would
 * apply one workspace's visit to another's queue.
 */
export function inboxVisitKey(userId: string, tenantId: string): string {
  return `${VISIT_KEY}:${userId}:${tenantId}`
}

/** Enough of `Storage` to read and write one string, so tests can hand in a fake. */
export interface VisitStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/** Null in a private window, in a thumbnailer, or wherever site data is blocked. */
export function inboxVisitStorage(): VisitStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}

/** `undefined` means "no boundary" — the badge then counts everything, as it did before. */
export function readInboxVisit(storage: VisitStorage | null, key: string): string | undefined {
  try {
    const stored = storage?.getItem(key)
    if (!stored) return undefined
    const at = new Date(stored)
    return Number.isNaN(at.getTime()) ? undefined : at.toISOString()
  } catch {
    return undefined
  }
}

export function rememberInboxVisit(at: string, storage: VisitStorage | null, key: string): void {
  try {
    storage?.setItem(key, at)
  } catch {
    // A badge is not worth a crash on a browser that refuses to store anything.
  }
}
